import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAccessLogs, searchAccessLogs } from '../api/client';

// Shared single-log-group viewer (2026-08-07) - events from CloudWatch,
// used by each of the "InstapayApp Logs" dropdown's 5 pages. AccessLogs.jsx's
// own internal AccessLogCard (the "Access logs" page's two side-by-side
// cards) is left as-is, still today-only and without the search feature
// below - not touched here.
//
// Lazy-loading (2026-08-07 follow-up), replacing the original Previous/Next
// pagination: events accumulate across pages in one growing list instead of
// each page replacing the last. A sentinel row at the bottom of the
// scrollable table is watched by an IntersectionObserver, scoped to the
// scrollable container itself (not the viewport) - scrolling that row into
// view auto-loads the next page and appends it. A "Load more" button next
// to the sentinel calls the exact same function, as a manual fallback for
// anyone who'd rather click than scroll (and so this isn't solely dependent
// on IntersectionObserver actually firing correctly).
//
// "Search all dates" toggle (2026-08-13) - originally always scoped to
// today (Manila time) only, per direct user request this now has an
// opt-in checkbox that switches the query to every date CloudWatch still
// has for this log group (see accessLogs.js's `allDates` option on the BFF
// side). Toggling it behaves exactly like flipping `source`: it resets the
// accumulated event list and cursor and starts a fresh fetch from scratch,
// since "today" and "all dates" are two different underlying CloudWatch
// queries with their own pagination cursors, not something that can be
// patched onto an in-progress today-only fetch.
//
// Real search (2026-08-13 follow-up) - the text box below was, until now,
// only a client-side substring filter over `events` (whatever pages
// getAccessLogs() has already fetched into the browser). That's fine for
// skimming what's on screen, but a real gap was observed the same day: a
// CloudWatch Logs Insights query in the AWS console found a match instantly
// that this filter box could NOT find even with "Search all dates" on,
// because those particular lines were ~55 minutes further back than the
// newest 1500 events already loaded - the filter box literally never saw
// them. "Search all logs" (button, next to the same text input) instead
// calls searchAccessLogs(), which runs a real CloudWatch Logs Insights
// query server-side (accessLogsSearch.js) across the full selected date
// scope and returns every match in one response, same as the AWS console
// does. While a search is active, the table shows searchResults instead of
// the live/loaded `events` list; editing the query text (or flipping
// source/allDates) drops back to live-browse mode, since a stale search
// result set next to a changed query or date scope would be actively
// misleading. The two live/search modes are otherwise independent - the
// plain-typing filter behavior on `events` is unchanged for anyone who
// isn't reaching for "Search all logs".
//
// `source` must be a key of the BFF's ACCESS_LOG_SOURCES map (index.js) -
// see data/instapayAppLogSources.js for the 5 slugs this component is used
// with.

const STATUS_LABELS = {
  ok: null,
  unavailable: 'This log group is not configured for this environment.',
  error: 'CloudWatch Logs request failed.',
};

// Search has its own status vocabulary (accessLogsSearch.js) - 'complete'
// instead of 'ok', plus 'timed_out' (the Logs Insights query didn't finish
// within the BFF's own poll budget - no partial results to show, just ask
// the person to retry or narrow the search) on top of the 'unavailable'/
// 'error' statuses it shares with the browse view.
const SEARCH_STATUS_LABELS = {
  complete: null,
  unavailable: 'This log group is not configured for this environment.',
  timed_out: 'Search took too long to finish. Try a more specific search, or try again.',
  error: 'CloudWatch Logs Insights search failed.',
};

const MIN_SEARCH_LENGTH = 2;

function formatManilaTime(ms) {
  if (!ms) {
    return '—';
  }
  return new Date(ms).toLocaleString('en-PH', {
    timeZone: 'Asia/Manila',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function LogGroupCard({ source }) {
  const [events, setEvents] = useState([]);
  const [logGroup, setLogGroup] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [query, setQuery] = useState('');
  const [allDates, setAllDates] = useState(false);

  // Search mode state (2026-08-13) - kept entirely separate from the live
  // `events` list above; `searchActive` is what switches the table between
  // the two. `searchStatus` mirrors accessLogsSearch.js's response
  // `status` field directly ('complete'/'unavailable'/'timed_out'/'error'),
  // plus the local-only 'loading' while the request is in flight.
  const [searchActive, setSearchActive] = useState(false);
  const [searchStatus, setSearchStatus] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState(null);
  const [searchRecordsScanned, setSearchRecordsScanned] = useState(null);
  const [searchTruncated, setSearchTruncated] = useState(false);
  const [searchedFor, setSearchedFor] = useState('');

  // The CloudWatch pagination cursor for the NEXT fetch - a ref, not state,
  // since it's only ever read at the moment a fetch happens, never rendered.
  const nextTokenRef = useRef(undefined);
  // Guards against two overlapping fetches (e.g. the IntersectionObserver
  // firing again before the in-flight request resolves).
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);
  // Bumped on every handleSearch()/clearSearch() call - a search request in
  // flight checks this against the id it captured before awaiting, and
  // drops its own result if it no longer matches (the user edited the
  // query, cleared the search, or fired a second search before the first
  // one resolved). Without this, a slow first search whose text has since
  // changed could resolve AFTER a newer search/edit and silently overwrite
  // it with stale results.
  const searchIdRef = useRef(0);

  function clearSearch() {
    searchIdRef.current += 1;
    setSearchActive(false);
    setSearchStatus(null);
    setSearchResults([]);
    setSearchError(null);
    setSearchRecordsScanned(null);
    setSearchTruncated(false);
    setSearchedFor('');
  }

  const fetchPage = useCallback(
    async (reset) => {
      if (loadingRef.current) {
        return;
      }
      loadingRef.current = true;
      if (reset) {
        setLoadingInitial(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      try {
        const token = reset ? undefined : nextTokenRef.current;
        const data = await getAccessLogs(source, token, allDates);
        setEvents((prev) => (reset ? data.events || [] : [...prev, ...(data.events || [])]));
        setLogGroup(data.log_group);
        setStartTime(data.start_time);
        setStatus(data.status);
        setHasMore(Boolean(data.has_more));
        nextTokenRef.current = data.next_token || undefined;
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load logs.');
      } finally {
        setLoadingInitial(false);
        setLoadingMore(false);
        loadingRef.current = false;
      }
    },
    [source, allDates]
  );

  // Reruns on `source` changing (unchanged) and also on `allDates` toggling
  // (2026-08-13) - both reset the accumulated list and cursor and start a
  // fresh fetch, since "today" and "all dates" are two distinct CloudWatch
  // queries with their own pagination cursors. Also drops out of search
  // mode (2026-08-13 follow-up) - a search result set from before the
  // source/date-scope change would no longer correspond to what's selected.
  useEffect(() => {
    nextTokenRef.current = undefined;
    setEvents([]);
    setHasMore(false);
    fetchPage(true);
    clearSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, allDates]);

  function handleRefresh() {
    nextTokenRef.current = undefined;
    setEvents([]);
    setHasMore(false);
    fetchPage(true);
  }

  async function handleSearch() {
    const q = query.trim();
    if (q.length < MIN_SEARCH_LENGTH || searchStatus === 'loading') {
      return;
    }
    const id = (searchIdRef.current += 1);
    // Switch into search mode immediately (not only once the response
    // comes back) so the table shows a "Searching…" state right away
    // instead of continuing to display live/filtered events while the
    // request is in flight.
    setSearchActive(true);
    setSearchStatus('loading');
    setSearchError(null);
    setSearchedFor(q);
    try {
      const data = await searchAccessLogs(source, q, allDates);
      if (searchIdRef.current !== id) {
        return; // superseded by a newer search, a query edit, or clearSearch()
      }
      setSearchResults(data.events || []);
      setSearchRecordsScanned(typeof data.records_scanned === 'number' ? data.records_scanned : null);
      setSearchTruncated(Boolean(data.truncated));
      setSearchStatus(data.status || 'error');
      setSearchError(data.status === 'error' ? data.error || 'Search failed.' : null);
    } catch (err) {
      if (searchIdRef.current !== id) {
        return;
      }
      setSearchResults([]);
      setSearchStatus('error');
      setSearchError(err.message || 'Search failed.');
    }
  }

  function handleQueryChange(e) {
    setQuery(e.target.value);
    // Typing invalidates whatever's currently shown as search results (it
    // no longer matches the box) - drop back to live-browse mode/filtering
    // rather than leaving a stale result set on screen. Re-run "Search all
    // logs" explicitly to search again.
    if (searchActive) {
      clearSearch();
    }
  }

  function handleQueryKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  }

  // Auto-load the next page once the sentinel row scrolls into view within
  // the scrollable table container (root) - not the whole browser viewport,
  // which would trigger a load the moment the card itself is on screen.
  // Doesn't apply in search mode - search results are a single complete
  // (if capped) response, not something paginated the same way.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || searchActive) {
      return undefined;
    }
    const root = el.closest('.table-responsive') || null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loadingRef.current) {
          fetchPage(false);
        }
      },
      { root, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, fetchPage, searchActive]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return events;
    }
    return events.filter((e) => (e.message || '').toLowerCase().includes(q));
  }, [events, query]);

  const statusNote = status ? STATUS_LABELS[status] : null;
  const searchStatusNote = searchStatus && searchStatus !== 'loading' ? SEARCH_STATUS_LABELS[searchStatus] : null;

  // Which rows/status/error actually drive the table below - search mode
  // takes over the same table rather than duplicating it.
  const rows = searchActive ? searchResults : filtered;
  const rowsLoading = searchActive ? searchStatus === 'loading' : loadingInitial && events.length === 0;
  const rowsError = searchActive ? (searchStatus === 'error' ? searchError : null) : error;
  const rowsUnavailable = searchActive ? searchStatus === 'unavailable' : status === 'unavailable';

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-1">
          <h2 className="h6 mb-0">Logs</h2>
          <div className="d-flex align-items-center gap-3">
            <div className="form-check form-check-inline mb-0">
              <input
                type="checkbox"
                className="form-check-input"
                id={`all-dates-${source}`}
                checked={allDates}
                onChange={(e) => setAllDates(e.target.checked)}
                disabled={loadingInitial || loadingMore}
              />
              <label className="form-check-label small" htmlFor={`all-dates-${source}`}>
                Search all dates
              </label>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleRefresh}
              disabled={loadingInitial}
            >
              {loadingInitial ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
        <p className="text-muted small mb-3">
          {startTime && (
            allDates ? (
              <>All available dates, from {formatManilaTime(startTime)} (Asia/Manila)</>
            ) : (
              <>Today from {formatManilaTime(startTime)} (Asia/Manila)</>
            )
          )}
        </p>

        {!searchActive && error && <div className="alert alert-danger py-2 small">{error}</div>}
        {!searchActive && !error && statusNote && <div className="alert alert-warning py-2 small">{statusNote}</div>}

        {status !== 'unavailable' && (
          // input-group (2026-08-13, was a plain d-flex row) - a bare flex
          // row left the input's width:100% (Bootstrap's own .form-control
          // CSS) competing with the buttons for space, which could leave
          // "Search all logs" cramped or hard to notice depending on window
          // width - flagged after a report where the search never actually
          // fired because the button wasn't easily clickable. .input-group
          // is Bootstrap's own pattern for exactly this input+button combo
          // - the input gets flex:1 1 auto and the buttons keep their
          // natural size, with no custom flex-basis fighting needed.
          <div className="input-group input-group-sm mb-2">
            <input
              type="text"
              className="form-control"
              placeholder="Filter loaded logs… or search all logs"
              value={query}
              onChange={handleQueryChange}
              onKeyDown={handleQueryKeyDown}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleSearch}
              disabled={query.trim().length < MIN_SEARCH_LENGTH || searchStatus === 'loading'}
              title={
                query.trim().length < MIN_SEARCH_LENGTH
                  ? `Type at least ${MIN_SEARCH_LENGTH} characters to search all logs`
                  : 'Search every date-scoped log line for this text, not just what has loaded so far'
              }
            >
              {searchStatus === 'loading' ? 'Searching…' : 'Search all logs'}
            </button>
            {searchActive && (
              <button type="button" className="btn btn-outline-secondary" onClick={clearSearch}>
                Clear search
              </button>
            )}
          </div>
        )}

        {searchActive && searchStatus === 'error' && (
          <div className="alert alert-danger py-2 small">{searchError}</div>
        )}
        {searchActive && searchStatus !== 'error' && searchStatusNote && (
          <div className="alert alert-warning py-2 small">{searchStatusNote}</div>
        )}

        {rowsLoading && rows.length === 0 && !rowsError ? (
          <div className="d-flex align-items-center gap-2 text-muted py-3">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            {searchActive ? 'Searching…' : 'Loading…'}
          </div>
        ) : (
          !rowsError &&
          !rowsUnavailable && (
            <div className="table-responsive" style={{ maxHeight: '32rem', overflowY: 'auto' }}>
              <table className="table table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '11rem' }}>Time (Manila)</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e, idx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={`${e.timestamp}-${idx}`}>
                      <td className="text-nowrap text-muted">{formatManilaTime(e.timestamp)}</td>
                      <td className="font-monospace text-break" style={{ fontSize: '0.75rem' }}>
                        {e.message}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && !rowsLoading && (
                    <tr>
                      <td colSpan={2} className="text-center text-muted py-4">
                        {searchActive
                          ? `No matches for “${searchedFor}”.`
                          : events.length === 0
                            ? (allDates ? 'No log events found.' : 'No log events for today yet.')
                            : 'No log lines match this filter.'}
                      </td>
                    </tr>
                  )}
                  {/* Lazy-load sentinel - only in live-browse mode, only
                      rendered once there's an initial result set, so it
                      never fires before the first real page has loaded. */}
                  {!searchActive && events.length > 0 && (
                    <tr ref={sentinelRef}>
                      <td colSpan={2} className="text-center text-muted py-3">
                        {loadingMore ? (
                          <span className="d-flex align-items-center justify-content-center gap-2">
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            Loading more…
                          </span>
                        ) : hasMore ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => fetchPage(false)}
                          >
                            Load more
                          </button>
                        ) : (
                          allDates ? 'End of log lines.' : 'End of today’s log lines.'
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )
        )}

        {searchActive && searchStatus === 'complete' && searchResults.length > 0 && (
          <p className="text-muted small mt-2 mb-0">
            Found {searchResults.length}{searchTruncated ? '+' : ''} match{searchResults.length === 1 ? '' : 'es'} for “
            {searchedFor}”{typeof searchRecordsScanned === 'number' ? ` (${searchRecordsScanned.toLocaleString()} records scanned)` : ''}
            {searchTruncated ? ' — showing the most recent 1000; narrow your search for the rest.' : '.'}
          </p>
        )}

        {!searchActive && !error && status !== 'unavailable' && events.length > 0 && (
          <p className="text-muted small mt-2 mb-0">
            {query.trim()
              ? `Found ${filtered.length} of ${events.length} loaded events`
              : `Showing ${events.length} loaded events`}
            {hasMore ? ' (more available)' : ''}.
          </p>
        )}
      </div>
    </div>
  );
}
